import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Printer, Search } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { PageHero } from "../../components/Bits";

const inputCls = "w-full rounded-md border border-gray-300 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-saffron-500 focus:ring-2 focus:ring-saffron-500/30";

export default function PrintReceipt() {
  const [ref, setRef] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const lookup = async (e) => {
    e.preventDefault();
    if (!ref.trim() || !contact.trim()) { toast.error("Enter the donation/receipt number and your mobile or email."); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/public/donations/lookup-receipt", { ref: ref.trim(), contact: contact.trim() });
      if (r.data?.receipt_token) { navigate(`/donate/receipt/${r.data.receipt_token}`); return; }
      setResult(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white" data-testid="print-receipt-page">
      <SEO title="Print Receipt" />
      <PageHero title="Print Receipt" sub="Retrieve your donation receipt securely" />
      <section className="py-12">
        <div className="container-ssf max-w-xl">
          <form onSubmit={lookup} className="card-ssf space-y-5 p-6 sm:p-8" data-testid="print-receipt-form">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-navy">Donation Number / Receipt Number <span className="text-red-500">*</span></label>
              <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. SSF-DON-2026-0001 or SSF-RCPT-2026-0001" className={inputCls} data-testid="pr-ref" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-navy">Mobile Number or Email <span className="text-red-500">*</span></label>
              <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Registered mobile number or email" className={inputCls} data-testid="pr-contact" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full !rounded-md !py-3 text-sm disabled:opacity-50" data-testid="pr-submit">
              <Search size={15} /> {busy ? "VERIFYING…" : "FIND MY RECEIPT"}
            </button>
            <p className="text-[11px] leading-relaxed text-slate-400">For your security, receipts are shown only when the reference and your registered mobile/email both match. Receipts exist only for successfully confirmed donations.</p>
          </form>

          {result && (
            <div className="card-ssf mt-6 p-6 sm:p-8" data-testid="pr-result">
              <dl className="divide-y divide-gray-100 text-sm">
                {[["Donor", result.name], ["Donation Number", result.don_no], ["Receipt Number", result.receipt_no || "—"],
                  ["Amount", `₹${Number(result.amount).toLocaleString("en-IN")}`],
                  ["Status", "PAID"], ["Payment Date", result.payment_time ? new Date(result.payment_time).toLocaleString("en-IN") : "—"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2">
                    <dt className="text-slate-500">{k}</dt>
                    <dd className={`text-right font-semibold ${k === "Status" ? "text-brandgreen" : "text-navy"}`}>{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 flex gap-3">
                <a href={`/api/public/donations/${encodeURIComponent(result.don_no)}/receipt`} target="_blank" rel="noreferrer" className="btn-primary flex-1 !rounded-md !py-3 text-center text-sm" data-testid="pr-download">
                  <Download size={15} className="mr-1 inline" /> Download Receipt
                </a>
                <button onClick={() => window.print()} className="btn-dark flex-1 !rounded-md !py-3 text-sm" data-testid="pr-print">
                  <Printer size={15} className="mr-1 inline" /> Print
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
